/**
 * Renders the Verify root-cause sub-agent inline, attached to its CHILD harness run.
 * Chromeless — the timeline is the UI. Its assistant prose is the caller's
 * source-grounded block, passed through to the timeline as `renderAssistantText`.
 */

import { cn } from '@heidi/utils';
import { useIntl } from 'react-intl';

import { projectRunFailureMessages } from '../../../../data/projects/components/projectRunFailure.messages';
import type { BlockStatus } from '../../../../data/projects/timeline/presentation/types';
import { agentHarnessClient } from '../../../agent-harness/client';
import { useRunStreamLifecycle } from '../../../agent-harness/hooks';
import { VerifyChildTimeline, type VerifyChildTimelineHost } from './VerifyChildTimeline';
import { CancelledNotice, ErrorNotice, StartingPlaceholder } from './VerifyInlineStatus';

import type { JSX } from 'react';

export interface VerifyChildRunInlineProps {
  childAgentRunId: string;
  status: BlockStatus;
  question?: string;
  allowMount: boolean;
  className?: string;
}

export const VerifyChildRunInline = ({
  childAgentRunId,
  status,
  question,
  allowMount,
  className,
  renderAssistantText,
}: VerifyChildRunInlineProps & VerifyChildTimelineHost): JSX.Element => {
  if (!allowMount) {
    return (
      <StartingPlaceholder status={status} question={question} className={className} />
    );
  }
  return (
    <VerifyChildRunLifecycle
      childAgentRunId={childAgentRunId}
      status={status}
      question={question}
      className={className}
      renderAssistantText={renderAssistantText}
    />
  );
};

interface VerifyChildRunLifecycleProps extends VerifyChildTimelineHost {
  childAgentRunId: string;
  status: BlockStatus;
  question?: string;
  className?: string;
}

const VerifyChildRunLifecycle = ({
  childAgentRunId,
  status,
  question,
  className,
  renderAssistantText,
}: VerifyChildRunLifecycleProps): JSX.Element | null => {
  const intl = useIntl();
  const { items, isLoading } = useRunStreamLifecycle(childAgentRunId, agentHarnessClient);
  const isStreaming = status === 'running';

  if (items.length === 0) {
    // A finished child's transcript is still loading (a reload): it has not
    // been seen to produce nothing yet, so it says nothing until it arrives.
    if (isLoading && status === 'success') return null;
    return (
      <StartingPlaceholder
        status={status}
        question={question}
        isLoading={isLoading || isStreaming}
        className={className}
      />
    );
  }

  return (
    <div
      className={cn('flex w-full flex-col gap-2', className)}
      data-testid="verify-child-inline"
      data-child-run-id={childAgentRunId}
    >
      <VerifyChildTimeline
        items={items}
        isStreaming={isStreaming}
        renderAssistantText={renderAssistantText}
      />
      {status === 'error' && (
        <ErrorNotice
          message={intl.formatMessage(projectRunFailureMessages.evidencePartialResults)}
        />
      )}
      {status === 'cancelled' && <CancelledNotice question={question} />}
    </div>
  );
};
